// What a re-send of a lost approve needs first: a hold with enough editing lease left (KARNI follow-up, 2026-10-31).
//
// A lost approve (thrown / aborted, then status 410 claim_unknown) never reached the server's approve script, so its
// hold is still `editing` and the server's /api/face-claims/renew accepts it while the lease is alive (minime-web
// face_claims.py _RENEW). At KARNI (2026-10-25) three guests lost their approve ~91 s after the tap, with ~14 s of
// the 105 s lease left: the old rule (re-send only with >= 20 s left) sent nothing and offered nothing.
//
// prepareResend() decides, in order:
//   1. no claim token (reusable heads / legacy approve): nothing is held, so the same request is re-sent with the
//      full bound.
//   2. renew the hold. OK => the same request (same request id, same token), its lease now ending at
//      renew-send-time + lease_s (the send time, never the reply time, so the client never counts past the server);
//      an OK that leaves too little lease => "offline" (the hold is still this request's own: a re-acquire without
//      its token would meet faces_held).
//      409 processing => the approve WAS recorded meanwhile: the page polls it instead of sending.
//      410 claim_revoked => an operator released the hold: the page's existing Home return.
//      410 (expired / gone / lost) => step 3.
//      Network / 5xx => the same request on the lease the page still holds, when enough is left; else "offline".
//   3. acquire the SAME faces again (new acquire request id, no prior token: the old one is expired). OK => a NEW
//      request (new approve request id and tap time, the new token), persisted before it is returned.
//      409 faces_held / processing and other refusals => "taken" (the guest chooses again). event_not_running =>
//      "ended". Network / 5xx => "offline", and the next call reuses the same acquire request id, so a reply that
//      was lost in transit is replayed by the server instead of leaving a second hold. A replay that finds its
//      earlier hold ended (410 claim_expired) or nearly ended asks once more under a NEW acquire request id (with
//      the nearly-ended hold's token as the prior token: a swap). An acquire that lands after the guest left the
//      visit (`isLive` false) releases its hold and writes nothing.
//
// ADR 018 deviation (owner-approved 2026-10-31): the silent re-send renews once without a guest tap; the hold may be
// acquired again from the result page. Never throws.
import {
  acquireFaces,
  currentGuestId,
  currentGuestSource,
  newRequestId,
  releaseClaim,
  renewClaim,
  writeClaimState,
} from "./faceClaimClient";
import { APPROVE_LEASE, APPROVE_TIMEOUT_MS, resendTimeoutMs } from "./approveRetry";

export const RECOVERY = Object.freeze({
  RESEND: "resend",
  PROCESSING: "processing",
  REVOKED: "revoked",
  TAKEN: "taken",
  ENDED: "ended",
  OFFLINE: "offline",
});

// The bound of the re-acquire (the renew has its own, faceClaimClient RENEW_TIMEOUT_MS): a timed-out acquire is
// "offline", and the next attempt replays the same acquire request id.
export const REACQUIRE_TIMEOUT_MS = 15000;

const isTransient = (result) => result.status === 0 || result.status >= 500;

const leaseEndAfter = (sentAt, result) => {
  const leaseS = Number(result.body && result.body.lease_s);
  return Number.isFinite(leaseS) && leaseS > 0 ? sentAt + leaseS * 1000 : null;
};

const acquireAgain = async (event, request, isLive, retried = false) => {
  const faces = request.claimState && Array.isArray(request.claimState.faces) ? request.claimState.faces : [];
  const guest = currentGuestId();
  if (!faces.length || !guest) return { kind: RECOVERY.TAKEN };
  const replay = Boolean(request.reacquireRid);
  const acqRid = request.reacquireRid || newRequestId();
  const sentAt = Date.now();
  const result = await acquireFaces(event, {
    faces,
    requestId: acqRid,
    userId: guest,
    guestSource: currentGuestSource(),
    timeoutMs: REACQUIRE_TIMEOUT_MS,
    ...(request.swapToken ? { claimToken: request.swapToken } : {}),
  });
  const fresh = (extra) => {
    const next = { ...request, ...extra };
    delete next.reacquireRid;
    return acquireAgain(event, next, isLive, true);
  };
  if (result.ok && (result.code === "editing" || result.code === "shared")) {
    const token = result.code === "editing" ? result.body.claim_token || null : null;
    const leaseEndAt = token ? leaseEndAfter(sentAt, result) : null;
    const timeoutMs = token ? resendTimeoutMs(leaseEndAt) : APPROVE_TIMEOUT_MS;
    if (timeoutMs === null) {
      // A replayed acquire answered with the hold it made earlier, but little of its lease is left: swap that hold
      // for a fresh one (same faces, its token as the prior token) under a NEW acquire request id, once.
      if (replay && token && !retried) return fresh({ swapToken: token });
      return { kind: RECOVERY.OFFLINE, request: { ...request, reacquireRid: acqRid } };
    }
    if (!event.isCurrent() || (isLive && !isLive())) {
      // The guest left while the acquire was out (flow expiry, Back, another event): do not leave a hold or a
      // claim record behind for a visit that is over.
      if (token) releaseClaim(event, token, { keepalive: true });
      return { kind: RECOVERY.ENDED };
    }
    const approveRid = newRequestId();
    const claimState = {
      token,
      faces,
      acqRid,
      acqKey: [...faces].sort().join("|"),
      acqPrior: null,
      approveRid,
      approveAt: Date.now(),
    };
    writeClaimState(event.eventName, claimState); // persisted BEFORE the new approve, as at a first tap
    const next = { ...request, approveRid, claimToken: token || undefined, claimState };
    delete next.reacquireRid;
    delete next.swapToken;
    return { kind: RECOVERY.RESEND, request: next, leaseEndAt, timeoutMs, lease: APPROVE_LEASE.REACQUIRED };
  }
  if (isTransient(result)) return { kind: RECOVERY.OFFLINE, request: { ...request, reacquireRid: acqRid } };
  // A replayed acquire whose earlier hold has ended answers claim_expired (the request-id tombstone): the faces may
  // well be free, so ask once more under a NEW request id instead of telling the guest the selection is gone.
  if (replay && !retried && result.status === 410 && result.code === "claim_expired") {
    return fresh({ swapToken: undefined });
  }
  if (result.code === "event_not_running") return { kind: RECOVERY.ENDED };
  if (result.status === 410 && result.code === "claim_revoked") return { kind: RECOVERY.REVOKED };
  return { kind: RECOVERY.TAKEN };
};

// `request` is the page's approve request ({ approveRid, claimToken, claimState, ... }); `leaseEndAt` the lease end
// the page holds for it (null = unknown); `isLive` (optional) the page's own "this visit is still current" check.
// Resolves { kind, request?, leaseEndAt?, timeoutMs?, lease? }.
export const prepareResend = async ({ event, request, leaseEndAt, isLive }) => {
  if (!event || !request) return { kind: RECOVERY.ENDED };
  try {
    if (!request.claimToken) {
      // A re-acquire that already failed in transit keeps its request id: finish THAT acquisition first.
      if (request.reacquireRid) return await acquireAgain(event, request, isLive);
      return { kind: RECOVERY.RESEND, request, leaseEndAt: null, timeoutMs: APPROVE_TIMEOUT_MS, lease: null };
    }
    const sentAt = Date.now();
    const renewed = await renewClaim(event, request.claimToken);
    if (renewed.ok) {
      const end = leaseEndAfter(sentAt, renewed);
      const timeoutMs = resendTimeoutMs(end);
      if (timeoutMs !== null) {
        return { kind: RECOVERY.RESEND, request, leaseEndAt: end, timeoutMs, lease: APPROVE_LEASE.RENEWED };
      }
      // A renewal that leaves too little lease (not reachable with today's 105 s lease and 10 s throttle): the hold
      // is still this request's own, so a re-acquire without its token would meet faces_held. Offer the button.
      return { kind: RECOVERY.OFFLINE, request };
    }
    if (renewed.status === 409 && renewed.code === "processing") return { kind: RECOVERY.PROCESSING, request };
    if (renewed.status === 410 && renewed.code === "claim_revoked") return { kind: RECOVERY.REVOKED };
    if (renewed.status === 410) return await acquireAgain(event, { ...request, claimToken: undefined }, isLive);
    if (isTransient(renewed)) {
      const timeoutMs = resendTimeoutMs(leaseEndAt);
      if (timeoutMs !== null) return { kind: RECOVERY.RESEND, request, leaseEndAt, timeoutMs, lease: null };
      return { kind: RECOVERY.OFFLINE, request };
    }
    return { kind: RECOVERY.ENDED };
  } catch (error) {
    return { kind: RECOVERY.OFFLINE, request };
  }
};
